import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, ChevronDown, Facebook } from 'lucide-react';
import { businessInfo, images } from '../../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { 
      name: 'Services', 
      path: '/services',
      dropdown: [
        { name: 'All Services', path: '/services' },
        { name: 'Nutritional Therapy', path: '/nutritional-therapy' },
        { name: 'Detox Support', path: '/detox-support' },
        { name: 'Pure Renewal Protocol', path: '/pure-renewal-protocol' },
      ]
    },
    { name: 'Orientation Classes', path: '/orientation' },
    { name: 'Classes & Events', path: '/classes' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Top Bar */}
      <div className="hidden md:block bg-[#2C2F36] text-white py-2">
        <div className="container-custom">
          <div className="flex justify-between items-center text-sm">
            <div className="flex items-center gap-6">
              <a href={`tel:${businessInfo.phone}`} className="flex items-center gap-2 text-white/90 hover:text-white transition-colors">
                <Phone size={14} />
                {businessInfo.phone}
              </a>
              <a href={`mailto:${businessInfo.email}`} className="flex items-center gap-2 text-white/90 hover:text-white transition-colors">
                <Mail size={14} />
                {businessInfo.email}
              </a>
            </div>
            <div className="flex items-center gap-6 text-white/90">
              <a 
                href={businessInfo.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 hover:text-white transition-colors"
              >
                <Facebook size={14} />
                <span>Facebook</span>
              </a>
              <div className="flex items-center gap-2">
                <MapPin size={14} />
                <span>{businessInfo.address}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header 
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled 
            ? 'bg-white/95 backdrop-blur-md shadow-md' 
            : 'bg-white'
        }`}
      >
        <div className="container-custom">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <img 
                src={images.logo} 
                alt="Serenity Natural Health & Wellness"
                className="h-16 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                link.dropdown ? (
                  <div 
                    key={link.name}
                    className="relative group"
                    onMouseEnter={() => setIsServicesOpen(true)}
                    onMouseLeave={() => setIsServicesOpen(false)}
                  >
                    <button
                      onClick={() => setIsServicesOpen(!isServicesOpen)}
                      className={`px-4 py-3 text-sm font-medium transition-colors flex items-center gap-1 ${
                        location.pathname.includes('/services') || 
                        location.pathname.includes('/nutritional') || 
                        location.pathname.includes('/detox') ||
                        location.pathname.includes('/pure-renewal')
                          ? 'text-[#6E9EB9]'
                          : 'text-[#2C2F36] hover:text-[#6E9EB9]'
                      }`}
                    >
                      {link.name}
                      <ChevronDown size={14} className={`transition-transform duration-200 ${isServicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                    
                    {isServicesOpen && (
                      <div className="absolute top-full left-0 pt-2">
                        <div className="bg-white rounded-lg shadow-lg py-2 min-w-[220px] border border-gray-100">
                          {link.dropdown.map((item) => (
                            <Link
                              key={item.name}
                              to={item.path}
                              className={`block px-4 py-2.5 text-sm transition-colors ${
                                isActive(item.path)
                                  ? 'text-[#6E9EB9] bg-[#6E9EB9]/5'
                                  : 'text-[#2C2F36] hover:text-[#6E9EB9] hover:bg-[#6E9EB9]/5'
                              }`}
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <Link
                    key={link.name}
                    to={link.path}
                    className={`px-4 py-3 text-sm font-medium transition-colors ${
                      isActive(link.path)
                        ? 'text-[#6E9EB9]'
                        : 'text-[#2C2F36] hover:text-[#6E9EB9]'
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
            </nav>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <Link to="/contact" className="btn-primary text-sm">
                Book Consultation
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 text-[#2C2F36]"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100">
            <div className="container-custom py-4">
              <nav className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  link.dropdown ? (
                    <div key={link.name}>
                      <div className="px-4 py-3 text-sm font-semibold text-[#6E9EB9] uppercase tracking-wider">
                        {link.name}
                      </div>
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.name}
                          to={item.path}
                          className={`block px-6 py-2.5 text-sm transition-colors ${
                            isActive(item.path)
                              ? 'text-[#6E9EB9] bg-[#6E9EB9]/5'
                              : 'text-[#2C2F36]'
                          }`}
                        >
                          {item.name}
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <Link
                      key={link.name}
                      to={link.path}
                      className={`px-4 py-3 text-sm font-medium transition-colors ${
                        isActive(link.path)
                          ? 'text-[#6E9EB9] bg-[#6E9EB9]/5'
                          : 'text-[#2C2F36]'
                      }`}
                    >
                      {link.name}
                    </Link>
                  )
                ))}
              </nav>
              
              <div className="mt-4 pt-4 border-t border-gray-100">
                <Link to="/contact" className="btn-primary w-full text-sm">
                  Book Consultation
                </Link>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-100 space-y-3 text-sm text-[#4A5568]">
                <a href={`tel:${businessInfo.phone}`} className="flex items-center gap-2">
                  <Phone size={16} className="text-[#6E9EB9]" />
                  {businessInfo.phone}
                </a>
                <a href={`mailto:${businessInfo.email}`} className="flex items-center gap-2">
                  <Mail size={16} className="text-[#6E9EB9]" />
                  {businessInfo.email}
                </a>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;